import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import {
  Shield,
  RefreshCw,
  Download,
  Search,
  AlertTriangle,
  Users,
  CheckCircle2,
  UserPlus,
  Mail,
  Clock,
  X,
  Trash2,
  Copy,
} from 'lucide-react';
import { TopNav } from '../ui/top-nav';
import { PermissionsPivot } from './PermissionsPivot';
import { accessControlService, Invite } from '../../services/accessControlService';
import { fetchAuthSession } from 'aws-amplify/auth';

interface UserData {
  user_id: string;
  email: string;
  features: Record<string, boolean>;
  updated_at?: string;
}

export const AccessControlPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Invite state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [isInviting, setIsInviting] = useState(false);
  const [sentInvites, setSentInvites] = useState<Invite[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadUsers();
    loadSentInvites();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const session = await fetchAuthSession();
      const userId = session.tokens?.idToken?.payload?.sub as string;
      setCurrentUserId(userId);
    } catch (err) {
      console.error('Error getting current user:', err);
    }
  };

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(
        users.filter(user =>
          user.email.toLowerCase().includes(term) ||
          user.user_id.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loadedUsers = await accessControlService.listUsersWithPermissions();
      setUsers(loadedUsers);
      setFilteredUsers(loadedUsers);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadUsers();
    setIsRefreshing(false);
    toast.success('Users refreshed');
  };

  const handlePermissionChange = async (userId: string, feature: string, enabled: boolean) => {
    // Optimistic update
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.user_id === userId
          ? {
              ...user,
              features: {
                ...user.features,
                [feature]: enabled,
              },
            }
          : user
      )
    );

    try {
      await accessControlService.updatePermission(userId, feature, enabled);
    } catch (error) {
      // Revert on error
      await loadUsers();
      throw error;
    }
  };

  const handleBulkChange = async (userId: string, enableAll: boolean) => {
    if (enableAll) {
      await accessControlService.enableAllFeatures(userId);
    } else {
      await accessControlService.disableAllFeatures(userId);
    }
    await loadUsers();
  };

  const handleExportCSV = () => {
    try {
      accessControlService.downloadCSV(users);
      toast.success('Permissions exported to CSV');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const loadSentInvites = async () => {
    setIsLoadingInvites(true);
    try {
      const result = await accessControlService.getSentInvites();
      if (result.success && result.invites) {
        setSentInvites(result.invites);
      }
    } catch (err) {
      console.error('Error loading sent invites:', err);
    } finally {
      setIsLoadingInvites(false);
    }
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Clipboard API failed:', err);
      }
    }

    // Fallback: create a temporary textarea
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (err) {
      console.error('Fallback copy failed:', err);
      return false;
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsInviting(true);
    try {
      const result = await accessControlService.createInvite(inviteEmail.trim(), inviteRole);
      if (result.success) {
        const inviteLink = result.invite_link || `https://bigsplicer.com/login?invite=${result.invite_id}`;
        const copied = await copyToClipboard(inviteLink);
        if (copied) {
          toast.success(`Invitation link copied for ${inviteEmail}`);
        } else {
          toast.info(
            <div className="space-y-1">
              <p>Invite created! Copy this link:</p>
              <code className="text-xs bg-muted p-1 rounded block break-all">{inviteLink}</code>
            </div>,
            { duration: 10000 }
          );
        }
        setInviteEmail('');
        setInviteRole('member');
        setShowInviteDialog(false);
        loadSentInvites();
      } else {
        toast.error(result.error || 'Failed to create invitation');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to create invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (userId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from your organization?`)) {
      return;
    }

    try {
      const result = await accessControlService.removeMember(userId);
      if (result.success) {
        toast.success(`${email} has been removed`);
        loadUsers();
      } else {
        toast.error(result.error || 'Failed to remove member');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove member');
    }
  };

  const handleCopyInviteLink = async (inviteId: string, email: string) => {
    const inviteLink = `https://bigsplicer.com/login?invite=${inviteId}`;
    const success = await copyToClipboard(inviteLink);
    if (success) {
      toast.success(`Invite link copied for ${email}`);
    } else {
      // Show the link in a toast so user can manually copy
      toast.info(
        <div className="space-y-1">
          <p>Could not copy automatically. Copy this link:</p>
          <code className="text-xs bg-muted p-1 rounded block break-all">{inviteLink}</code>
        </div>,
        { duration: 10000 }
      );
    }
  };

  const getTotalUsers = () => users.length;
  const getAdminCount = () => users.filter(u => u.features?.admin === true).length;
  const getActiveUsers = () => {
    return users.filter(u => {
      if (!u.features) return false;
      const enabledCount = Object.values(u.features).filter(v => v === true).length;
      return enabledCount > 0;
    }).length;
  };

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto p-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Header */}
        <div className="flex flex-col gap-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-primary" />
              <div>
                <h1 className="text-base font-semibold text-foreground">Access Control</h1>
                <p className="text-sm text-muted-foreground">
                  Manage user permissions and feature access
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setShowInviteDialog(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={users.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{getTotalUsers()}</div>
                <p className="text-xs text-muted-foreground">
                  Registered accounts
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{getActiveUsers()}</div>
                <p className="text-xs text-muted-foreground">
                  With at least one permission
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Administrators</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{getAdminCount()}</div>
                <p className="text-xs text-muted-foreground">
                  Users with admin access
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Admin Notice */}
        <Alert className="mb-6 border-amber-500/50 bg-amber-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <AlertDescription className="text-sm">
              <strong>Admin Access Required:</strong> This page is only accessible to users with admin permissions.
              Changes to permissions take effect immediately.
            </AlertDescription>
          </div>
        </Alert>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by email or user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchTerm && (
            <p className="text-sm text-muted-foreground mt-2">
              Showing {filteredUsers.length} of {users.length} users
            </p>
          )}
        </div>

        {/* Error State */}
        {error && !isLoading && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {error}
              <Button
                variant="outline"
                size="sm"
                onClick={loadUsers}
                className="ml-4"
              >
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Permissions Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permissions Matrix
            </CardTitle>
            <CardDescription>
              Enable or disable features for each user. Click checkboxes to toggle individual permissions,
              or use the "All" and "None" buttons for bulk changes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PermissionsPivot
              users={filteredUsers}
              isLoading={isLoading}
              onPermissionChange={handlePermissionChange}
              onBulkChange={handleBulkChange}
              onRefresh={handleRefresh}
              onRemoveMember={handleRemoveMember}
              showRemoveButton={users.length > 1}
              currentUserId={currentUserId}
            />
          </CardContent>
        </Card>

        {/* Sent Invitations */}
        {sentInvites.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-5 w-5" />
                Pending Invitations
              </CardTitle>
              <CardDescription>
                Invitations you have sent that are awaiting response
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sentInvites.filter(inv => inv.status === 'pending').map((invite) => (
                  <div
                    key={invite.invite_id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{invite.invitee_email}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>Sent {new Date(invite.created_at).toLocaleDateString()}</span>
                          <Badge variant="outline" className="text-xs">
                            {invite.role}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyInviteLink(invite.invite_id, invite.invitee_email || '')}
                        title="Copy invitation link"
                      >
                        <Copy className="h-3 w-3" />
                        <span className="ml-1">Copy Link</span>
                      </Button>
                      <Badge variant="secondary">
                        {invite.status}
                      </Badge>
                    </div>
                  </div>
                ))}
                {sentInvites.filter(inv => inv.status === 'pending').length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No pending invitations
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Text */}
        <Card className="mt-6 bg-muted/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Feature Descriptions</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Dashboard:</strong>
                <span className="text-muted-foreground">View and manage files</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Upload:</strong>
                <span className="text-muted-foreground">Upload new data files</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Analyze:</strong>
                <span className="text-muted-foreground">Query and analyze data</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Visualize:</strong>
                <span className="text-muted-foreground">Create visualizations</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Introspect:</strong>
                <span className="text-muted-foreground">AI-powered insights</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Developer API:</strong>
                <span className="text-muted-foreground">Manage API keys</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">AI Connections:</strong>
                <span className="text-muted-foreground">MCP server integration</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Integrations:</strong>
                <span className="text-muted-foreground">Third-party integrations</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Usage:</strong>
                <span className="text-muted-foreground">View usage statistics</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Profile:</strong>
                <span className="text-muted-foreground">Manage account settings</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Stats Dashboard:</strong>
                <span className="text-muted-foreground">Platform statistics</span>
              </div>
              <div className="flex gap-1">
                <strong className="text-foreground whitespace-nowrap">Admin:</strong>
                <span className="text-muted-foreground">Admin features</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Invite User Dialog */}
      {showInviteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Invite User
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowInviteDialog(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Create an invitation link to share with a new user
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email Address</label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={isInviting}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Role</label>
                <div className="flex gap-2">
                  <Button
                    variant={inviteRole === 'member' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInviteRole('member')}
                    disabled={isInviting}
                    className="flex-1"
                  >
                    Member
                  </Button>
                  <Button
                    variant={inviteRole === 'admin' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInviteRole('admin')}
                    disabled={isInviting}
                    className="flex-1"
                  >
                    Admin
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {inviteRole === 'admin'
                    ? 'Admins can manage permissions and invite other users'
                    : 'Members have access based on their assigned permissions'}
                </p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowInviteDialog(false)}
                  disabled={isInviting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInviteUser}
                  disabled={isInviting || !inviteEmail.trim()}
                  className="flex-1"
                >
                  {isInviting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Create & Copy Link
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
